/**
 * Bulk Delivery cards: one action applied to many dates or requests (every
 * approved waiver, a sheet of COR answers). Shanks expands the rows and says
 * which it refused; the browser applies the rest a few at a time, saves its
 * progress as it goes, and treats a row someone already handled as skipped.
 */ /** One for_each group, as propose-delivery-action summarizes it. */ /** How many rows apply at once. Each is a two-person proposal RPC. */ export const BULK_CONCURRENCY = 4;
/** Progress is saved this often, so a closed tab never re-proposes a row. */ export const BULK_CHECKPOINT_EVERY = 50;
const ALREADY_OPEN = /already has a decision awaiting approval|only one change can be open/i;
const ALREADY_DECIDED = /not awaiting approval|is (applied|rejected|cancelled)\b|already (approved|applied|decided|rejected|withdrawn)|invalid state transition/i;
/**
 * The words for a row the server refused because someone already handled
 * it: a request is already open on the date, or the request was already
 * decided. Such a row is skipped, not failed, so Retry never re-sends it and
 * re-accepting a card cannot propose the same change twice. Null for any
 * other error.
 */ export function alreadyHandled(message) {
    // A waiver edit proposed against an older waiver fails; it is not handled.
    if (/changed after this edit was proposed/i.test(message)) return null;
    if (ALREADY_OPEN.test(message)) return 'Already has a request awaiting approval';
    if (ALREADY_DECIDED.test(message)) return 'Already decided';
    return null;
}
/**
 * Applies rows a few at a time, in index order. onRow sees each outcome as it
 * lands; onCheckpoint runs every `checkpointEvery` rows and once at the end.
 */ export async function runBulkApply(indices, applyOne, opts = {}) {
    const concurrency = Math.max(1, opts.concurrency ?? BULK_CONCURRENCY);
    const every = Math.max(1, opts.checkpointEvery ?? BULK_CHECKPOINT_EVERY);
    const out = new Map();
    let next = 0;
    let sinceCheckpoint = 0;
    let checkpoint = Promise.resolve();
    const worker = async ()=>{
        while(next < indices.length){
            const index = indices[next++];
            const result = await applyOne(index);
            out.set(index, result);
            opts.onRow?.(index, result);
            sinceCheckpoint++;
            if (sinceCheckpoint >= every && opts.onCheckpoint) {
                sinceCheckpoint = 0;
                // Checkpoints run one after another, never overlapping.
                checkpoint = checkpoint.then(()=>opts.onCheckpoint?.()).catch(()=>undefined);
            }
        }
    };
    await Promise.all(Array.from({
        length: Math.min(concurrency, indices.length)
    }, worker));
    await checkpoint;
    return out;
}
/** Rows grouped by deliverable, in the card's order, with their date span. */ export function groupByDeliverable(rows) {
    const groups = new Map();
    rows.forEach((row, i)=>{
        const key = row.deliverable || 'Other';
        let g = groups.get(key);
        if (!g) {
            g = {
                deliverable: key,
                rows: []
            };
            groups.set(key, g);
        }
        g.rows.push(i);
        const due = row.dueOn || '';
        if (due) {
            if (!g.first || due < g.first) g.first = due;
            if (!g.last || due > g.last) g.last = due;
        }
    });
    return [
        ...groups.values()
    ];
}
/** "746 changes · 12 not proposed · 3 already as asked" for a group header. */ export function bulkGroupLine(g) {
    const parts = [
        `${g.proposed} ${g.proposed === 1 ? 'change' : 'changes'}`
    ];
    if (g.refusedCount) parts.push(`${g.refusedCount} not proposed`);
    if (g.unchangedCount) parts.push(`${g.unchangedCount} already as asked`);
    if (g.partial) parts.push(`part of the rows (${g.partial})`);
    return parts.join(' · ');
}
